import { useState } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@eriko/ui/src/components/ui/card';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Input } from '@eriko/ui/src/components/ui/input';
import { Label } from '@eriko/ui/src/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@eriko/ui/src/components/ui/select';
import { useUkTaxSettings, useUpdateUkTaxSettings } from '../../../../hooks/useUkTax';
// Toast on save success/error is handled in useUpdateUkTaxSettings hook (useUkTax.ts)

const VAT_SCHEMES = [
  { value: 'standard', label: 'Standard' },
  { value: 'flat_rate', label: 'Flat Rate' },
  { value: 'cash', label: 'Cash Accounting' },
];

const LEGAL_STRUCTURES = [
  { value: 'sole_trader', label: 'Sole Trader' },
  { value: 'ltd', label: 'Limited Company (Ltd)' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'llp', label: 'Limited Liability Partnership (LLP)' },
];

const VAT_STAGGER_GROUPS = [
  { value: 'group_1', label: 'Group 1 (Mar / Jun / Sep / Dec)' },
  { value: 'group_2', label: 'Group 2 (Apr / Jul / Oct / Jan)' },
  { value: 'group_3', label: 'Group 3 (May / Aug / Nov / Feb)' },
];

interface UkTaxSettingsData {
  utr: string | null;
  niNumber: string | null;
  vatRegistered: boolean;
  vatNumber: string | null;
  vatScheme: string | null;
  vatStaggerGroup: string | null;
  businessLegalStructure: string | null;
  taxYearStart: string | null;
  privateUseVehiclePercent: number | null;
  privateUseHomeOfficePercent: number | null;
  isCisRegistered: boolean;
}

export function UkTaxSettings() {
  const { data: settings, isLoading } = useUkTaxSettings();

  if (isLoading || !settings) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return <UkTaxSettingsForm settings={settings} />;
}

function UkTaxSettingsForm({ settings }: { settings: UkTaxSettingsData }) {
  const updateSettings = useUpdateUkTaxSettings();
  const [utr, setUtr] = useState(settings.utr ?? '');
  const [niNumber, setNiNumber] = useState(settings.niNumber ?? '');
  const [vatRegistered, setVatRegistered] = useState(settings.vatRegistered ? 'yes' : 'no');
  const [vatNumber, setVatNumber] = useState(settings.vatNumber ?? '');
  const [vatScheme, setVatScheme] = useState(settings.vatScheme ?? '');
  const [vatStaggerGroup, setVatStaggerGroup] = useState(settings.vatStaggerGroup ?? '');
  const [legalStructure, setLegalStructure] = useState(settings.businessLegalStructure ?? '');
  const [vehiclePercent, setVehiclePercent] = useState(
    settings.privateUseVehiclePercent?.toString() ?? '',
  );
  const [homeOfficePercent, setHomeOfficePercent] = useState(
    settings.privateUseHomeOfficePercent?.toString() ?? '',
  );
  const [cisRegistered, setCisRegistered] = useState(settings.isCisRegistered ? 'yes' : 'no');
  const [taxYearStartMode, setTaxYearStartMode] = useState<'april_6' | 'custom'>(
    settings.taxYearStart && settings.taxYearStart !== 'april_6' ? 'custom' : 'april_6',
  );
  const [taxYearStartCustom, setTaxYearStartCustom] = useState(
    settings.taxYearStart && settings.taxYearStart !== 'april_6' ? settings.taxYearStart : '',
  );

  const isVatRegistered = vatRegistered === 'yes';
  const isCis = cisRegistered === 'yes';

  const hasChanges =
    utr !== (settings.utr ?? '') ||
    niNumber !== (settings.niNumber ?? '') ||
    (vatRegistered === 'yes') !== settings.vatRegistered ||
    vatNumber !== (settings.vatNumber ?? '') ||
    vatScheme !== (settings.vatScheme ?? '') ||
    vatStaggerGroup !== (settings.vatStaggerGroup ?? '') ||
    legalStructure !== (settings.businessLegalStructure ?? '') ||
    vehiclePercent !== (settings.privateUseVehiclePercent?.toString() ?? '') ||
    homeOfficePercent !== (settings.privateUseHomeOfficePercent?.toString() ?? '') ||
    (cisRegistered === 'yes') !== settings.isCisRegistered ||
    taxYearStartMode !== (settings.taxYearStart && settings.taxYearStart !== 'april_6' ? 'custom' : 'april_6') ||
    (taxYearStartMode === 'custom' && taxYearStartCustom !== (settings.taxYearStart && settings.taxYearStart !== 'april_6' ? settings.taxYearStart : ''));

  function handleSave() {
    updateSettings.mutate({
      utr: utr || null,
      niNumber: niNumber || null,
      vatRegistered: isVatRegistered,
      vatNumber: isVatRegistered ? (vatNumber || null) : null,
      vatScheme: isVatRegistered ? (vatScheme || null) : null,
      vatStaggerGroup: isVatRegistered ? (vatStaggerGroup || null) : null,
      businessLegalStructure: legalStructure || null,
      taxYearStart: taxYearStartMode === 'custom' ? (taxYearStartCustom || null) : null,
      privateUseVehiclePercent: vehiclePercent ? Number(vehiclePercent) : null,
      privateUseHomeOfficePercent: homeOfficePercent ? Number(homeOfficePercent) : null,
      isCisRegistered: isCis,
    });
  }

  return (
    <div className="space-y-6">
      {/* Tax Identity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tax Identity</CardTitle>
          <CardDescription>
            Your UTR and National Insurance number for Self Assessment filing.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="utr">UTR (Unique Taxpayer Reference)</Label>
              <Input
                id="utr"
                value={utr}
                onChange={(e) => setUtr(e.target.value)}
                placeholder="1234567890"
                maxLength={10}
              />
              <p className="text-xs text-muted-foreground">10-digit Unique Taxpayer Reference</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="niNumber">National Insurance Number</Label>
              <Input
                id="niNumber"
                value={niNumber}
                onChange={(e) => setNiNumber(e.target.value)}
                placeholder="e.g. QQ 12 34 56 C"
                maxLength={13}
              />
              <p className="text-xs text-muted-foreground">National Insurance number (2 letters, 6 digits, 1 letter)</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="legalStructure">Business Legal Structure</Label>
            <Select value={legalStructure} onValueChange={setLegalStructure}>
              <SelectTrigger id="legalStructure" className="w-64">
                <SelectValue placeholder="Select structure" />
              </SelectTrigger>
              <SelectContent>
                {LEGAL_STRUCTURES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="taxYearStart">Tax Year Start</Label>
            <Select value={taxYearStartMode} onValueChange={(v) => setTaxYearStartMode(v as 'april_6' | 'custom')}>
              <SelectTrigger id="taxYearStart" className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="april_6">Standard (6 April)</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            {taxYearStartMode === 'custom' && (
              <div className="mt-2">
                <Input
                  id="taxYearStartCustom"
                  value={taxYearStartCustom}
                  onChange={(e) => setTaxYearStartCustom(e.target.value)}
                  placeholder="MM-DD"
                  maxLength={5}
                  className="w-32"
                />
                <p className="text-xs text-muted-foreground mt-1">Enter date in MM-DD format (e.g. 01-01 for 1 January)</p>
              </div>
            )}
            <p className="text-xs text-muted-foreground">Most sole traders use the standard 6 April start. When unset, the system assumes standard.</p>
          </div>
        </CardContent>
      </Card>

      {/* VAT */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">VAT Registration</CardTitle>
          <CardDescription>
            If you are VAT-registered, enter your VAT number and scheme details.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="vatRegistered">VAT Registered</Label>
            <Select value={vatRegistered} onValueChange={setVatRegistered}>
              <SelectTrigger id="vatRegistered" className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isVatRegistered && (
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="vatNumber">VAT Number</Label>
                <Input
                  id="vatNumber"
                  value={vatNumber}
                  onChange={(e) => setVatNumber(e.target.value)}
                  placeholder="GB 123 4567 89"
                  maxLength={14}
                />
                <p className="text-xs text-muted-foreground">9 digits, optionally prefixed with GB</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vatScheme">VAT Scheme</Label>
                <Select value={vatScheme} onValueChange={setVatScheme}>
                  <SelectTrigger id="vatScheme">
                    <SelectValue placeholder="Select scheme" />
                  </SelectTrigger>
                  <SelectContent>
                    {VAT_SCHEMES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vatStagger">VAT Stagger Group</Label>
                <Select value={vatStaggerGroup} onValueChange={setVatStaggerGroup}>
                  <SelectTrigger id="vatStagger">
                    <SelectValue placeholder="Select group" />
                  </SelectTrigger>
                  <SelectContent>
                    {VAT_STAGGER_GROUPS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Private Use */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Private Use Adjustments</CardTitle>
          <CardDescription>
            Percentage of vehicle and home office costs used privately (disallowed for tax).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="vehiclePercent">Vehicle Private Use (%)</Label>
              <Input
                id="vehiclePercent"
                type="number"
                min={0}
                max={100}
                value={vehiclePercent}
                onChange={(e) => setVehiclePercent(String(Math.min(100, Math.max(0, Number(e.target.value) || 0))))}
                placeholder="0"
                className="w-24"
              />
              <p className="text-xs text-muted-foreground">Business use percentage (0-100)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="homeOfficePercent">Home Office Private Use (%)</Label>
              <Input
                id="homeOfficePercent"
                type="number"
                min={0}
                max={100}
                value={homeOfficePercent}
                onChange={(e) => setHomeOfficePercent(String(Math.min(100, Math.max(0, Number(e.target.value) || 0))))}
                placeholder="0"
                className="w-24"
              />
              <p className="text-xs text-muted-foreground">Business use percentage (0-100)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* CIS */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">CIS Registration</CardTitle>
          <CardDescription>
            If you are a CIS-registered contractor, enable this to track subcontractor payments and deductions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="cisRegistered">CIS Registered</Label>
            <Select value={cisRegistered} onValueChange={setCisRegistered}>
              <SelectTrigger id="cisRegistered" className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || updateSettings.isPending}
          className="h-10 px-6 text-sm font-semibold"
        >
          {updateSettings.isPending ? 'Saving...' : 'Save tax settings'}
        </Button>
      </div>
    </div>
  );
}
